import { useState, useEffect, useCallback } from "react";
import { API_HOST } from "../constants";
import type { AssessmentResult, ConceptCategory, RoadmapArea } from "../data/ch_concepts";

type AssessmentState = "idle" | "loading_existing" | "quiz" | "generating" | "roadmap" | "error";

export interface GeneratingProgress {
  status: string;
  summary: string | null;
  areas: RoadmapArea[];
  tokenStream: string;  // raw LLM tokens for progressive label extraction
}

interface UseKnowledgeAssessmentReturn {
  state: AssessmentState;
  assessment: AssessmentResult | null;
  progress: GeneratingProgress | null;
  error: string | null;
  startQuiz: () => void;
  submitQuiz: (answers: Record<string, unknown>, userId: number) => Promise<void>;
  resetAssessment: () => void;
  fetchAdaptiveConcepts: (answers: Record<string, unknown>) => Promise<ConceptCategory[]>;
}

export function useKnowledgeAssessment(userId: number | null): UseKnowledgeAssessmentReturn {
  const [state, setState] = useState<AssessmentState>("idle");
  const [assessment, setAssessment] = useState<AssessmentResult | null>(null);
  const [progress, setProgress] = useState<GeneratingProgress | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Load existing assessment on mount
  useEffect(() => {
    if (!userId) return;
    setState("loading_existing");

    fetch(`${API_HOST}/api/users/${userId}/assessment/latest`)
      .then((res) => {
        if (res.status === 404) {
          setState("idle");
          return null;
        }
        if (!res.ok) throw new Error("Ошибка загрузки оценки");
        return res.json();
      })
      .then((data) => {
        if (data) {
          setAssessment(data);
          setState("roadmap");
        }
      })
      .catch(() => {
        setState("idle");
      });
  }, [userId]);

  const startQuiz = useCallback(() => {
    setState("quiz");
    setError(null);
  }, []);

  const submitQuiz = useCallback(
    async (answers: Record<string, unknown>, uid: number) => {
      setState("generating");
      setError(null);
      setProgress({ status: "Отправляю ответы...", summary: null, areas: [], tokenStream: "" });

      try {
        const res = await fetch(`${API_HOST}/api/ai/assessment/generate/stream`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ user_id: uid, quiz_answers: answers }),
        });

        if (!res.ok || !res.body) {
          const detail = await res.json().catch(() => ({}));
          throw new Error((detail as { detail?: string })?.detail ?? "Ошибка генерации");
        }

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        let assessmentId: number | null = null;

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split("\n");
          buffer = lines.pop() ?? "";

          for (const line of lines) {
            if (!line.startsWith("data: ")) continue;
            const raw = line.slice(6).trim();
            if (!raw) continue;

            let event: Record<string, unknown>;
            try {
              event = JSON.parse(raw);
            } catch {
              continue;
            }

            const type = event.type as string;

            if (type === "token") {
              setProgress((p) => ({
                ...(p ?? { status: "", summary: null, areas: [] }),
                tokenStream: (p?.tokenStream ?? "") + (event.text as string),
              }));
            } else if (type === "status") {
              setProgress((p) => ({ ...(p ?? { summary: null, areas: [], tokenStream: "" }), status: event.message as string }));
            } else if (type === "summary") {
              setProgress((p) => ({ ...(p ?? { status: "", areas: [], tokenStream: "" }), summary: event.summary as string }));
            } else if (type === "area") {
              setProgress((p) => ({
                ...(p ?? { status: "", summary: null, tokenStream: "" }),
                areas: [...(p?.areas ?? []), event.area as RoadmapArea],
              }));
            } else if (type === "done") {
              assessmentId = event.assessment_id as number;
            } else if (type === "error") {
              throw new Error(event.message as string);
            }
          }
        }

        // Загружаем полный assessment из БД
        if (assessmentId) {
          const full = await fetch(`${API_HOST}/api/users/${uid}/assessment/latest`).then((r) => r.json());
          setAssessment(full);
        }

        setProgress(null);
        setState("roadmap");
      } catch (e) {
        const raw = e instanceof Error ? e.message : "Неизвестная ошибка";
        const isNetworkFailure =
          /failed to fetch|network error|networkerror|load failed|connection/i.test(raw);
        setError(
          isNetworkFailure
            ? "Не удалось связаться с сервером. Проверьте интернет и попробуйте снова."
            : raw
        );
        setProgress(null);
        setState("error");
      }
    },
    []
  );

  const fetchAdaptiveConcepts = useCallback(
    async (answers: Record<string, unknown>): Promise<ConceptCategory[]> => {
      const res = await fetch(`${API_HOST}/api/ai/assessment/concepts`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          experience: answers.experience ?? "never",
          scale: answers.scale ?? "",
          role: answers.role ?? "",
          motivation: answers.motivation ?? "",
        }),
      });
      if (!res.ok) throw new Error("Failed to fetch adaptive concepts");
      return res.json();
    },
    []
  );

  const resetAssessment = useCallback(() => {
    if (userId) {
      fetch(`${API_HOST}/api/users/${userId}/assessment`, { method: "DELETE" }).catch(() => {});
      try {
        const keysToRemove: string[] = [];
        for (let i = 0; i < sessionStorage.length; i++) {
          const key = sessionStorage.key(i);
          if (key?.startsWith("topic_content::")) keysToRemove.push(key);
        }
        keysToRemove.forEach((k) => sessionStorage.removeItem(k));
      } catch {
        /* ignore */
      }
    }
    setAssessment(null);
    setError(null);
    setProgress(null);
    setState("quiz");
  }, [userId]);

  return { state, assessment, progress, error, startQuiz, submitQuiz, resetAssessment, fetchAdaptiveConcepts };
}
